import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import StatusBadge from '../../components/invoice/StatusBadge';
import invoiceService from '../../services/invoiceService';
import { formatDateTime, formatDuration, formatMonthLabel } from '../../utils/invoiceFormatters';

const InvoiceJobHistory = ({ source = 'ECMS' }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchJobs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await invoiceService.getAllJobs(source);
      setJobs(res.data || []);
    } catch {
      setError('Failed to load generation history.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchJobs(); }, [source]);

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/admin/${source.toLowerCase()}/invoices`)}
              className="text-gray-400 hover:text-gray-600 text-sm">← Dashboard</button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{source === 'IFLEET' ? 'iFleet Generation History' : 'ECMS Generation History'}</h1>
              <p className="text-gray-500 text-sm">{jobs.length} job runs</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {loading ? (
            <div className="space-y-0">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-14 border-b border-gray-50 px-5 flex items-center gap-4">
                  <div className="h-3 bg-gray-100 rounded animate-pulse flex-1" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-20" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-16" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-gray-400 text-sm mb-3">{error}</p>
              <button onClick={fetchJobs}
                className="text-sm text-primary border border-primary px-4 py-1.5 rounded-lg hover:bg-primary/5">
                Retry
              </button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              No generation runs yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                  <th className="text-left px-5 py-3 font-medium">Job ID</th>
                  <th className="text-left px-5 py-3 font-medium">Type</th>
                  <th className="text-left px-5 py-3 font-medium">Period</th>
                  <th className="text-right px-4 py-3 font-medium">Total</th>
                  <th className="text-right px-4 py-3 font-medium">Generated</th>
                  <th className="text-right px-4 py-3 font-medium">Skipped</th>
                  <th className="text-right px-4 py-3 font-medium">Failed</th>
                  <th className="text-center px-4 py-3 font-medium">Status</th>
                  <th className="text-left px-4 py-3 font-medium">Started At</th>
                  <th className="text-right px-4 py-3 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {jobs.map((job) => (
                  <tr
                    key={job.id}
                    onClick={() => navigate(`/admin/${source.toLowerCase()}/invoices/jobs/${job.id}`)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-5 py-3 font-mono text-xs text-gray-400">#{job.id}</td>
                    <td className="px-5 py-3">
                      <StatusBadge status={job.jobType} size="xs" />
                    </td>
                    <td className="px-5 py-3 font-medium text-gray-700">
                      {formatMonthLabel(job.invoiceMonth, job.invoiceYear)}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">{job.totalEnterprises ?? 0}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: '#27500A' }}>
                      {job.generatedCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{job.skippedCount ?? 0}</td>
                    <td className="px-4 py-3 text-right" style={{ color: job.failedCount > 0 ? '#A32D2D' : '#6B7280' }}>
                      {job.failedCount ?? 0}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <StatusBadge status={job.status} pulse={job.status === 'RUNNING'} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{formatDateTime(job.startedAt)}</td>
                    <td className="px-4 py-3 text-right text-xs text-gray-400">
                      {job.status === 'RUNNING' ? '—' : formatDuration(job.durationSeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceJobHistory;
